// modules/WorldInteraction/WorldPlayerInteraction.js
// 世界场景玩家交互模块 - 处理多人模式下的玩家间交互

export default class WorldPlayerInteraction {
    constructor(scene) {
        this.scene = scene;
    }
    
    /**
     * 获取最近的玩家
     * @param {number} playerX - 玩家X坐标
     * @param {number} playerY - 玩家Y坐标
     * @param {number} maxDistance - 最大距离
     * @returns {Object|null} 最近的玩家精灵
     */
    getNearestPlayer(playerX, playerY, maxDistance) {
        if (!this.scene.multiplayerPlayerManager) return null;
        
        const remotePlayers = this.scene.multiplayerPlayerManager.getRemotePlayers();
        let nearestPlayer = null;
        let minDistance = maxDistance;
        
        remotePlayers.forEach(sprite => {
            const distance = Phaser.Math.Distance.Between(
                playerX, playerY,
                sprite.x, sprite.y
            );
            
            if (distance < minDistance) {
                minDistance = distance;
                nearestPlayer = sprite;
            }
        });
        
        return nearestPlayer;
    }
    
    /**
     * 显示玩家交互菜单
     * @param {Phaser.GameObjects.Sprite} playerSprite - 玩家精灵
     */
    showPlayerInteractionMenu(playerSprite) {
        const playerId = playerSprite.getData('playerId');
        const playerName = playerSprite.getData('playerName') || '玩家';
        
        if (!playerId) return;
        
        const centerX = this.scene.cameras.main.centerX;
        const centerY = this.scene.cameras.main.centerY;
        
        // 背景遮罩
        const overlay = this.scene.add.graphics();
        overlay.fillStyle(0x000000, 0.7);
        overlay.fillRect(0, 0, this.scene.cameras.main.width, this.scene.cameras.main.height);
        overlay.setDepth(200);
        overlay.setScrollFactor(0);
        overlay.setInteractive(new Phaser.Geom.Rectangle(0, 0, this.scene.cameras.main.width, this.scene.cameras.main.height), Phaser.Geom.Rectangle.Contains);
        
        // 对话框背景
        const dialogBg = this.scene.add.graphics();
        dialogBg.fillStyle(0x1a1a2e, 0.95);
        dialogBg.fillRoundedRect(centerX - 150, centerY - 100, 300, 200, 10);
        dialogBg.lineStyle(3, 0x4cc9f0, 1);
        dialogBg.strokeRoundedRect(centerX - 150, centerY - 100, 300, 200, 10);
        dialogBg.setDepth(201);
        dialogBg.setScrollFactor(0);
        
        // 标题
        const titleText = this.scene.add.text(
            centerX,
            centerY - 70,
            `与 ${playerName} 交互`,
            {
                fontSize: '18px',
                fill: '#4cc9f0',
                fontFamily: 'monospace',
                fontWeight: 'bold'
            }
        );
        titleText.setOrigin(0.5);
        titleText.setDepth(202);
        titleText.setScrollFactor(0);
        
        // 检查是否在队伍中
        const isInTeam = this.scene.teamManager && this.scene.teamManager.getTeamId();
        const isLeader = this.scene.teamManager && this.scene.teamManager.isTeamLeader();
        const teamMembers = this.scene.teamManager ? this.scene.teamManager.getTeamMembers() : [];
        const isInSameTeam = teamMembers.some(m => m.playerId === playerId);
        
        let buttonY = centerY - 30;
        const buttons = [];
        
        // 邀请加入队伍按钮（只有队长可以邀请，且对方不在队伍中）
        if (isInTeam && isLeader && !isInSameTeam) {
            const inviteButton = this.scene.add.text(
                centerX,
                buttonY,
                '邀请加入队伍',
                {
                    fontSize: '16px',
                    fill: '#ffffff',
                    fontFamily: 'monospace',
                    backgroundColor: '#00aa00',
                    padding: { x: 20, y: 8 }
                }
            );
            inviteButton.setOrigin(0.5);
            inviteButton.setDepth(202);
            inviteButton.setScrollFactor(0);
            inviteButton.setInteractive({ useHandCursor: true });
            buttons.push(inviteButton);
            
            inviteButton.on('pointerdown', () => {
                if (this.scene.teamManager) {
                    this.scene.teamManager.invitePlayer(playerId);
                }
                this.closeInteractionMenu(overlay, dialogBg, titleText, buttons);
            });
            
            buttonY += 40;
        }
        
        // 查看信息按钮
        const infoButton = this.scene.add.text(
            centerX,
            buttonY,
            '查看信息',
            {
                fontSize: '16px',
                fill: '#ffffff',
                fontFamily: 'monospace',
                backgroundColor: '#4cc9f0',
                padding: { x: 20, y: 8 }
            }
        );
        infoButton.setOrigin(0.5);
        infoButton.setDepth(202);
        infoButton.setScrollFactor(0);
        infoButton.setInteractive({ useHandCursor: true });
        buttons.push(infoButton);
        
        infoButton.on('pointerdown', () => {
            this.scene.worldUI.showMessage(`${playerName} 的信息`);
            this.closeInteractionMenu(overlay, dialogBg, titleText, buttons);
        });
        
        buttonY += 40;
        
        // 取消按钮
        const cancelButton = this.scene.add.text(
            centerX,
            buttonY,
            '取消',
            {
                fontSize: '16px',
                fill: '#ffffff',
                fontFamily: 'monospace',
                backgroundColor: '#666666',
                padding: { x: 20, y: 8 }
            }
        );
        cancelButton.setOrigin(0.5);
        cancelButton.setDepth(202);
        cancelButton.setScrollFactor(0);
        cancelButton.setInteractive({ useHandCursor: true });
        buttons.push(cancelButton);
        
        cancelButton.on('pointerdown', () => {
            this.closeInteractionMenu(overlay, dialogBg, titleText, buttons);
        });
        
        // 点击遮罩关闭
        overlay.on('pointerdown', () => {
            this.closeInteractionMenu(overlay, dialogBg, titleText, buttons);
        });
    }
    
    /**
     * 关闭交互菜单
     * @param {Phaser.GameObjects.Graphics} overlay - 遮罩
     * @param {Phaser.GameObjects.Graphics} dialogBg - 对话框背景
     * @param {Phaser.GameObjects.Text} titleText - 标题文本
     * @param {Array} buttons - 按钮数组
     */
    closeInteractionMenu(overlay, dialogBg, titleText, buttons) {
        overlay.destroy();
        dialogBg.destroy();
        titleText.destroy();
        buttons.forEach(btn => btn.destroy());
    }
}

